import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  IconButton,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react';
import { ArrowLeft, ExternalLink, Mail, Pencil, Phone, Trash2, User2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteCompany, getCompany } from '../hooks/companies';
import { fetchLeadsWithUrl } from '../hooks/leads';
import { Company, getCompanyId, Lead } from '../utils/types';
import {
  CompanyEmptyState,
  CompanyField,
  CompanyHero,
  CompanyPrimaryButton,
  CompanySectionCard,
  CompanyShell,
  CompanyStatusBadge,
  companyAccent,
} from './companies/components/companyUi';

const normalizeCompany = (data: unknown): Company | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }
  return data as Company;
};

const formatWebsite = (website?: string) => {
  if (!website) {
    return '';
  }
  return website.startsWith('http') ? website : `https://${website}`;
};

type LeadsPageResponse = {
  success: boolean;
  data?: {
    results?: Lead[];
    next?: string | null;
  };
};

export default function CompanyDetailsPage() {
  const { orgId, companyId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [company, setCompany] = useState<Company | null>(null);
  const [linkedLeads, setLinkedLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !companyId) {
        return;
      }

      setLoading(true);
      setError(null);

      const companyResponse = await getCompany(orgId, companyId);
      const nextCompany = normalizeCompany(companyResponse.data);

      if (!companyResponse.success || !nextCompany) {
        setError('Could not load company details.');
        setLoading(false);
        return;
      }

      setCompany(nextCompany);

      const collectedLeads: Lead[] = [];
      let nextUrl: string | null | undefined = null;
      let page = 0;

      do {
        const leadsResponse: LeadsPageResponse = await fetchLeadsWithUrl(
          orgId,
          nextUrl ?? undefined,
        );
        if (!leadsResponse.success || !leadsResponse.data?.results) {
          break;
        }

        collectedLeads.push(...(leadsResponse.data.results as Lead[]));
        nextUrl = leadsResponse.data.next;
        page += 1;
      } while (nextUrl && page < 10);

      const filtered = collectedLeads.filter(
        (lead) => String(getCompanyId(lead.company)) === String(nextCompany.id),
      );

      setLinkedLeads(filtered);
      setLoading(false);
    };

    load();
  }, [companyId, orgId]);

  const isComplete = Boolean(company?.website && (company.email || company.phone));

  const overviewFields = useMemo(
    () => [
      { label: 'Website', value: company?.website || 'Missing' },
      { label: 'Email', value: company?.email || 'Missing' },
      { label: 'Phone', value: company?.phone || 'Missing' },
      { label: 'Address', value: company?.address || 'Missing' },
    ],
    [company],
  );

  const handleDelete = async () => {
    if (!orgId || !companyId) {
      return;
    }

    setDeleting(true);
    const response = await deleteCompany(orgId, companyId);
    setDeleting(false);

    if (!response.success) {
      toast({
        title: 'Could not delete company',
        description:
          typeof response.data === 'string'
            ? response.data
            : 'Something went wrong while deleting the company.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    toast({
      title: 'Company deleted',
      status: 'success',
      duration: 2500,
      isClosable: true,
    });
    navigate(`/organization/${orgId}/manage-companies`);
  };

  if (loading) {
    return (
      <CompanyShell compact>
        <CompanySectionCard title="Loading company" description="Please wait while the company profile is loading.">
          <Text color="gray.500">Loading company details...</Text>
        </CompanySectionCard>
      </CompanyShell>
    );
  }

  if (!company) {
    return (
      <CompanyShell compact>
        <CompanyEmptyState
          title="Company not found"
          description={error || 'This company could not be loaded.'}
          action={
            <Button borderRadius="16px" variant="outline" onClick={() => navigate(-1)}>
              Go back
            </Button>
          }
        />
      </CompanyShell>
    );
  }

  return (
    <CompanyShell compact>
      <CompanyHero
        eyebrow="Company profile"
        title={company.name}
        description="Review the company record, scan linked leads, and keep contact details up to date."
        primaryAction={
          <CompanyPrimaryButton
            leftIcon={<Pencil size={18} />}
            onClick={() =>
              navigate(`/organization/${orgId}/manage-companies/${company.id}/edit`)
            }
          >
            Edit Company
          </CompanyPrimaryButton>
        }
        secondaryAction={
          <HStack spacing={3}>
            <Button
              borderRadius="16px"
              variant="outline"
              leftIcon={<ArrowLeft size={16} />}
              onClick={() => navigate(`/organization/${orgId}/manage-companies`)}
            >
              Back
            </Button>
            {!isMobile ? (
              <IconButton
                aria-label="Delete company"
                icon={<Trash2 size={16} />}
                borderRadius="16px"
                colorScheme="red"
                variant="outline"
                onClick={() => setIsDeleteOpen(true)}
              />
            ) : null}
          </HStack>
        }
      />

      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
        <Stack spacing={6} gridColumn={{ xl: 'span 2' }}>
          <CompanySectionCard
            title="Overview"
            description="The core company details used across forms, lists, and linked records."
            action={<CompanyStatusBadge complete={isComplete} />}
          >
            <Stack spacing={5}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                {overviewFields.map((field) => (
                  <CompanyField key={field.label} label={field.label} value={field.value} />
                ))}
              </SimpleGrid>
              {company.website ? (
                <Link href={formatWebsite(company.website)} isExternal color={companyAccent} fontWeight="600">
                  Visit website <ExternalLink size={14} style={{ display: 'inline', marginLeft: 6 }} />
                </Link>
              ) : null}
            </Stack>
          </CompanySectionCard>

          <CompanySectionCard
            title="Linked leads"
            description="Lead records connected to this company."
            action={
              <Badge borderRadius="full" px={3} py={1} colorScheme="teal">
                {linkedLeads.length} linked
              </Badge>
            }
          >
            {linkedLeads.length === 0 ? (
              <Text color="gray.500" fontSize="sm">
                No leads are currently linked to this company.
              </Text>
            ) : (
              <Stack spacing={3}>
                {linkedLeads.map((lead) => (
                  <Box
                    key={lead.id}
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="18px"
                    p={4}
                    _hover={{ borderColor: 'teal.300', bg: 'gray.50' }}
                    transition="all 0.16s ease"
                  >
                    <Stack spacing={3}>
                      <HStack justify="space-between" align="flex-start">
                        <VStack align="flex-start" spacing={1}>
                          <Text fontWeight="700" color="gray.900">
                            {`${lead.title ?? ''} ${lead.first_name ?? ''} ${lead.second_name ?? ''}`.trim() ||
                              'Unnamed lead'}
                          </Text>
                          <HStack spacing={2} flexWrap="wrap">
                            <Badge borderRadius="full" px={3} py={1}>
                              {lead.stage || 'Unknown stage'}
                            </Badge>
                            <Badge borderRadius="full" px={3} py={1} colorScheme="orange">
                              {lead.level_of_interest_display || 'Interest unknown'}
                            </Badge>
                          </HStack>
                        </VStack>
                        <Button
                          size="sm"
                          borderRadius="14px"
                          variant="ghost"
                          onClick={() => navigate(`/organization/${orgId}/leads/${lead.id}`)}
                        >
                          View lead
                        </Button>
                      </HStack>
                      <HStack spacing={4} flexWrap="wrap">
                        <HStack spacing={2}>
                          <Mail size={14} color="#718096" />
                          <Text fontSize="sm" color="gray.600">
                            {lead.email || 'No email'}
                          </Text>
                        </HStack>
                        <HStack spacing={2}>
                          <Phone size={14} color="#718096" />
                          <Text fontSize="sm" color="gray.600">
                            {lead.phone || 'No phone'}
                          </Text>
                        </HStack>
                      </HStack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </CompanySectionCard>
        </Stack>

        <Stack spacing={6}>
          <CompanySectionCard
            title="Quick facts"
            description="A compact summary of this company record."
          >
            <Stack spacing={4}>
              <CompanyField label="Linked leads" value={String(linkedLeads.length)} />
              <CompanyField label="Profile status" value={isComplete ? 'Complete' : 'Needs details'} />
              <CompanyField label="Preferred contact" value={company.email || company.phone || 'Missing'} />
            </Stack>
          </CompanySectionCard>

          <CompanySectionCard
            title="Actions"
            description="Jump to the next common company tasks."
          >
            <VStack align="stretch" spacing={3}>
              <CompanyPrimaryButton
                leftIcon={<Pencil size={16} />}
                onClick={() =>
                  navigate(`/organization/${orgId}/manage-companies/${company.id}/edit`)
                }
              >
                Edit company
              </CompanyPrimaryButton>
              <Button
                borderRadius="16px"
                variant="outline"
                leftIcon={<User2 size={16} />}
                onClick={() => navigate(`/organization/${orgId}/leads-dashboard`)}
              >
                View leads
              </Button>
              <Button
                borderRadius="16px"
                variant="ghost"
                colorScheme="red"
                leftIcon={<Trash2 size={16} />}
                onClick={() => setIsDeleteOpen(true)}
              >
                Delete company
              </Button>
            </VStack>
          </CompanySectionCard>
        </Stack>
      </SimpleGrid>

      <Modal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete company?</ModalHeader>
          <ModalBody>
            <Text>
              This action cannot be undone. You are about to delete{' '}
              <Badge colorScheme="red">{company.name}</Badge>.
            </Text>
            {linkedLeads.length > 0 ? (
              <>
                <Divider my={4} />
                <Text fontSize="sm" color="gray.500">
                  This company currently has {linkedLeads.length} linked lead
                  {linkedLeads.length > 1 ? 's' : ''}. Confirm that deleting the
                  company is safe for your workflow.
                </Text>
              </>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDelete}
              isLoading={deleting}
              loadingText="Deleting"
            >
              Delete company
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </CompanyShell>
  );
}
